'use client';
import React, { useEffect, useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { CheckCircle, X, Edit2, Flag, MessageSquare, Loader2, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';

interface Submission {
  id: string;
  original_text?: string;
  spoken_sentence?: string;
  english_translation: string;
  dialect: string;
  location: string;
  category: string;
  submission_status: string;
  ai_confidence_score: number | null;
  ai_flags: any[];
  ai_suggestions: any[];
  created_at: string;
  audio_url?: string;
  mutauro_languages?: { name: string };
}

type ActionType = 'approve' | 'reject' | 'flag' | 'request_correction';

export default function MutauroValidatePage() {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [tab, setTab] = useState<'text' | 'audio'>('text');
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [toast, setToast] = useState('');
  const supabase = createClient();

  useEffect(() => { loadSubmissions(); }, [tab]);

  async function loadSubmissions() {
    setLoading(true);
    try {
      const table = tab === 'text' ? 'mutauro_text_submissions' : 'mutauro_audio_submissions';
      const { data } = await supabase
        .from(table)
        .select('*, mutauro_languages(name)')
        .eq('submission_status', 'pending')
        .order('created_at', { ascending: true })
        .limit(50);
      setSubmissions(data || []);
      setCurrent(0);
    } finally {
      setLoading(false);
    }
  }

  async function handleAction(action: ActionType) {
    const sub = submissions[current];
    if (!sub) return;
    setActionLoading(true);
    try {
      const table = tab === 'text' ? 'mutauro_text_submissions' : 'mutauro_audio_submissions';
      const statusMap: Record<ActionType, string> = {
        approve: 'human_approved',
        reject: 'human_rejected',
        flag: 'flagged',
        request_correction: 'needs_correction',
      };
      await supabase.from(table).update({
        submission_status: statusMap[action],
        validator_id: user?.id,
        validator_notes: notes || null,
        validated_at: new Date().toISOString(),
      }).eq('id', sub.id);

      setToast(`Submission ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : action === 'flag' ? 'flagged' : 'sent for correction'}!`);
      setTimeout(() => setToast(''), 3000);
      setNotes('');
      setShowNotes(false);
      setSubmissions(prev => prev.filter(s => s.id !== sub.id));
      if (current >= submissions.length - 1) setCurrent(Math.max(0, submissions.length - 2));
    } catch (e: any) {
      setToast('Error: ' + e.message);
    } finally {
      setActionLoading(false);
    }
  }

  const sub = submissions[current];
  const confidence = sub?.ai_confidence_score;
  const confColor = confidence == null ? '#888' : confidence >= 80 ? '#1A6B3A' : confidence >= 60 ? '#CC7700' : '#CC0000';

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono uppercase tracking-widest px-2 py-0.5 rounded"
              style={{ background: '#CC000018', color: '#CC0000', fontFamily: 'var(--font-mono)' }}>Mutauro AI</span>
          </div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Validator Dashboard</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
            Review AI-flagged submissions and approve, reject or request corrections.
          </p>
        </div>

        {toast && (
          <div className="mb-4 flex items-center gap-2 p-3 rounded-lg border text-sm"
            style={{ background: '#1A6B3A10', borderColor: '#1A6B3A40', color: '#1A6B3A' }}>
            <CheckCircle size={15} /> {toast}
          </div>
        )}

        {/* Tab */}
        <div className="flex gap-1 p-1 rounded-xl mb-5" style={{ background: 'var(--secondary)' }}>
          {(['text', 'audio'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className="flex-1 py-2 rounded-lg text-sm font-medium transition-all capitalize"
              style={{
                background: tab === t ? 'var(--card)' : 'transparent',
                color: tab === t ? 'var(--foreground)' : 'var(--muted-foreground)',
              }}>
              {t} Submissions
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin" style={{ color: '#CC0000' }} />
          </div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-16 rounded-xl border" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
            <CheckCircle size={32} className="mx-auto mb-3" style={{ color: '#1A6B3A' }} />
            <div className="font-semibold" style={{ color: 'var(--foreground)' }}>All caught up!</div>
            <div className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>No pending {tab} submissions to review.</div>
          </div>
        ) : (
          <div>
            {/* Progress */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                Reviewing {current + 1} of {submissions.length} pending
              </span>
              <div className="flex items-center gap-2">
                <button onClick={() => setCurrent(c => Math.max(0, c - 1))} disabled={current === 0}
                  className="p-1.5 rounded-lg border disabled:opacity-40 transition-colors"
                  style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
                  <ChevronLeft size={14} />
                </button>
                <button onClick={() => setCurrent(c => Math.min(submissions.length - 1, c + 1))} disabled={current === submissions.length - 1}
                  className="p-1.5 rounded-lg border disabled:opacity-40 transition-colors"
                  style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>

            {sub && (
              <div className="rounded-xl border overflow-hidden" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
                {/* Header */}
                <div className="px-5 py-4 border-b flex items-center justify-between flex-wrap gap-3"
                  style={{ borderColor: 'var(--border)', background: 'var(--secondary)' }}>
                  <div>
                    <div className="text-xs font-mono mb-0.5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>
                      Submission #{sub.id.slice(0, 8).toUpperCase()}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
                        Language: {sub.mutauro_languages?.name || 'Unknown'}
                      </span>
                      {sub.dialect && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>Dialect: {sub.dialect}</span>}
                      {sub.location && <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>📍 {sub.location}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-1 rounded-lg font-mono" style={{ background: '#CC000018', color: '#CC0000', fontFamily: 'var(--font-mono)' }}>
                      Pending Review
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 space-y-4">
                  <div>
                    <div className="text-xs font-medium mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                      {tab === 'text' ? 'Original Text' : 'Spoken Sentence'}
                    </div>
                    <div className="p-3 rounded-lg text-sm leading-relaxed" style={{ background: 'var(--secondary)', color: 'var(--foreground)' }}>
                      {tab === 'text' ? sub.original_text : sub.spoken_sentence}
                    </div>
                  </div>

                  {sub.english_translation && (
                    <div>
                      <div className="text-xs font-medium mb-1.5" style={{ color: 'var(--muted-foreground)' }}>English Translation</div>
                      <div className="p-3 rounded-lg text-sm" style={{ background: 'var(--secondary)', color: 'var(--foreground)' }}>
                        {sub.english_translation}
                      </div>
                    </div>
                  )}

                  {tab === 'audio' && sub.audio_url && (
                    <div>
                      <div className="text-xs font-medium mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Audio Recording</div>
                      <audio controls src={sub.audio_url} className="w-full rounded-lg" style={{ height: '40px' }} />
                    </div>
                  )}

                  {/* AI Confidence */}
                  <div className="rounded-lg p-3 border" style={{ background: '#00000005', borderColor: 'var(--border)' }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>AI Review</span>
                      {confidence != null && (
                        <span className="text-sm font-bold font-mono" style={{ color: confColor, fontFamily: 'var(--font-mono)' }}>
                          {confidence}% confidence
                        </span>
                      )}
                    </div>
                    {confidence != null && (
                      <div className="h-2 rounded-full overflow-hidden mb-2" style={{ background: 'var(--muted)' }}>
                        <div className="h-full rounded-full" style={{ width: `${confidence}%`, background: confColor }} />
                      </div>
                    )}
                    {sub.ai_flags?.length > 0 && (
                      <div className="space-y-1">
                        {sub.ai_flags.map((flag: any, i: number) => (
                          <div key={i} className="flex items-start gap-1.5 text-xs" style={{ color: '#CC7700' }}>
                            <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                            <span>{typeof flag === 'string' ? flag : JSON.stringify(flag)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(!sub.ai_flags || sub.ai_flags.length === 0) && confidence == null && (
                      <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>AI review pending for this submission.</p>
                    )}
                  </div>

                  {/* Validator Notes */}
                  {showNotes && (
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>Validator Notes</label>
                      <textarea
                        className="w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-red-200"
                        style={{ borderColor: 'var(--border)', background: 'var(--input)', color: 'var(--foreground)' }}
                        rows={2} placeholder="Add notes for the contributor…"
                        value={notes} onChange={e => setNotes(e.target.value)} />
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="px-5 py-4 border-t flex items-center gap-2 flex-wrap" style={{ borderColor: 'var(--border)', background: 'var(--secondary)' }}>
                  <button onClick={() => handleAction('approve')} disabled={actionLoading}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
                    style={{ background: '#1A6B3A', color: '#fff' }}>
                    {actionLoading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} Approve
                  </button>
                  <button onClick={() => handleAction('reject')} disabled={actionLoading}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-60"
                    style={{ borderColor: '#CC0000', color: '#CC0000', background: 'var(--card)' }}>
                    <X size={14} /> Reject
                  </button>
                  <button onClick={() => handleAction('request_correction')} disabled={actionLoading}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-60"
                    style={{ borderColor: 'var(--border)', color: 'var(--foreground)', background: 'var(--card)' }}>
                    <Edit2 size={14} /> Request Correction
                  </button>
                  <button onClick={() => handleAction('flag')} disabled={actionLoading}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-60"
                    style={{ borderColor: '#CC7700', color: '#CC7700', background: 'var(--card)' }}>
                    <Flag size={14} /> Flag
                  </button>
                  <button onClick={() => setShowNotes(v => !v)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition-colors ml-auto"
                    style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)', background: 'var(--card)' }}>
                    <MessageSquare size={14} /> Notes
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
